﻿import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import { AlertMessage, TranslationAdjustment } from '../../../components/common';
import { PageBackButton } from '../../home/PageBack';
import '../../home/homeSeriesContent.scss';
import VolumeAdjustmentPopup from '../../../components/common/VolumeAdjustmentPopup';
import { adjustTaskVolumeApi, backTranslateApi } from '../../../service/subtitles';
import { getLanguageName } from '../../../utils/languageUtils';
import { getTaskStatusApi } from '../../../service/task';
import { VideoShow } from '../videoShow';
import { VideoTextList } from '../videoTextList/VideoTextList';
import { VideoTimeBox } from './VideoTimeBox';
import { useVideoEditPage } from '../useVideoEditPage';
import '../videoEdit.scss';

const VideoEdit: React.FC = () => {
    const [searchParams] = useSearchParams();
    const langNameFromUrl = searchParams.get('langName')?.trim() || '';
    const {
        isAdjustingSpeed,
        alertConfig,
        hideAlert,
        isLoadingVideo,
        setIsLoadingVideo,
        videoData,
        setVideoData,
        setVideoDataSafe,
        isGenerating,
        showBackgroundVolumePopup,
        setShowBackgroundVolumePopup,
        backgroundVolume,
        setBackgroundVolume,
        backgroundVolumePopupPosition,
        setBackgroundVolumePopupPosition,
        ensureBackgroundGainNode,
        resumeBackgroundAudioContextIfSuspended,
        backgroundGainNodeRef,
        showError,
        showSuccess,
        videoName,
        isFromPure,
        handleBack,
        handleProofread,
        handleGenerate,
        handleSubtitlePostTextChange,
        handleTargetDialogueClick,
        clearGlobalRetranslateCloseTimer,
        scheduleCloseGlobalRetranslateDropdown,
        openGlobalRetranslateDropdown,
        isGlobalRetranslating,
        handleGlobalRetranslate,
        isArrowHovered,
        showGlobalRetranslateDropdown,
        setShowGlobalRetranslateDropdown,
        globalCustomText,
        setGlobalCustomText,
        currentEpisodeInfo,
        isBackTranslating,
        setIsBackTranslating,
        backTranslateData,
        setBackTranslateData,
        isGlobalRedubbing,
        handleGlobalRedub,
        textListRef,
        sourcesText,
        targetText,
        roles,
        rowSelectedRoles,
        editingRoleKey,
        editingRoleName,
        setEditingRoleName,
        seriesSpeakers,
        setSeriesSpeakers,
        highlightedSubtitleId,
        subtitleProcessingStates,
        updatingSubtitles,
        subtitleUpdateErrors,
        seriesVoiceLibraryMarkers,
        openHistorySubtitleId,
        volumeMap,
        speedMap,
        handleUpdateVolume,
        handleUpdateSpeed,
        handleSourceDataChange,
        handleTargetDataChange,
        handleRoleSelect,
        handleNewSpeaker,
        handleDeleteRole,
        handleStartEditRole,
        handleSaveRoleEdit,
        handleCancelRoleEdit,
        handlePromoteToSeriesSpeaker,
        seekToTime,
        pauseAudioForEditing,
        syncAudioAfterEditing,
        playSubtitle,
        playTargetSubtitle,
        handleUpdateVideoDataSpeed,
        handleSwitchTrack,
        handleDeleteSubtitle,
        handleAddSubtitle,
        handleSubtitleUpdate,
        handleGetNextSubtitle,
        handleGetNextSubtitleId,
        handleGetFullTimelineData,
        handleRefreshSubtitles,
        handleOpenHistoryPanel,
        handleCloseHistoryPanel,
        isAdmin,
        isUser,
        displayName,
        sourceVideoRef,
        handleTimeUpdate,
        handleLoadedMetadata,
        handleVideoEnded,
        setIsPlaying,
        ensureAudioPlayAndSync,
        userInteractionRef,
        backgroundAudioRef,
        vocalsAudioRef,
        vocalsHlsRef,
        subtitleOverlayPlayerRef,
        videoDataForDemo,
        localSubtitlesForDemo,
        targetTextForDemo,
        handleExternalPlay,
        handleExternalPause,
        handleExternalSeek,
        handleRightVideoReady,
        leftVideoReady,
        rightVideoReady,
        isPlaying,
        togglePlay,
        formatTime,
        duration,
        zoomLevel,
        handleZoomOut,
        handleZoomIn,
        handleZoomChange,
        handleZoomAfterChange,
        backgroundAudioEnabled,
        toggleBackgroundAudio,
        translatedTimelineModels,
        timelineActiveTrack,
        timelineSelectedSubtitleId,
        timelineEditorRef,
        playheadTotalTimeRef,
        timelineScrubbingRef,
        getTimelineTotalTimeSec,
        id,
        leftVideoAspect,
    } = useVideoEditPage();

    const orientation = leftVideoAspect
        ? (leftVideoAspect.ratio < 1 ? 'portrait' as const : 'landscape' as const)
        : 'portrait' as const;

    const videosAssetReady = leftVideoReady && rightVideoReady;
    const showPageGlass = isLoadingVideo || (!!videoData && !videosAssetReady);
    /** 无任务数据时右侧仅占位文案，避免挂载 overlay 播放器；左侧不再叠局部 loading，由全屏毛玻璃统一承担 */
    const rightCellLoadingUi = !videoData;

    return (
        <div className="video-edit-page">
            {/* 倍速调节加载遮罩*/}
            {isAdjustingSpeed && (
                <div className="adjusting-speed-overlay">
                    <div className="adjusting-speed-spinner" />
                </div>
            )}
            
            {/* Alert消息 */}
            <AlertMessage
                config={alertConfig}
                onClose={hideAlert}
                zIndex={1001}
            />

            {videoData?.status === 3 ? (
                /* 视频处理失败状态*/
                <div className="video-status-page">
                    <div className="video-status-card video-status-card--error">
                        <div className="video-status-icon video-status-icon--error">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h2 className="video-status-title video-status-title--error">
                            视频处理失败
                        </h2>
                        <p className="video-status-description">
                            抱歉，该视频处理过程中出现错误，无法进入编辑页面
                            <br />
                            请返回视频列表，重新上传或删除该视频
                        </p>
                        <div className="video-status-actions">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="video-status-button video-status-button--primary"
                            >
                                返回视频列表
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    // 刷新页面，重新获取视频状态
                                    window.location.reload();
                                }}
                                className="video-status-button video-status-button--success"
                            >
                                重新检查状态
                            </button>
                        </div>
                    </div>
                </div>
            ) : videoData != null && [0, 1, 4].includes(Number(videoData.status)) ? (
                /* 视频处理中状态*/
                <div className="video-status-page">
                    <div className="video-status-card video-status-card--processing">
                        <div className="video-status-icon video-status-icon--processing">
                            <i className="fas fa-spinner fa-spin"></i>
                        </div>
                        <h2 className="video-status-title video-status-title--processing">
                            {videoData?.status === 0 ? '视频等待中' : 
                             videoData?.status === 4 ? '视频排队中' : '视频处理中'}
                        </h2>
                        <p className="video-status-description">
                            {videoData?.status === 0 ? '视频正在等待处理，请稍等..' : 
                             videoData?.status === 4 ? '视频正在排队中，请稍等..' : '视频正在处理中，请稍等..'}
                            <br />
                            处理完成后即可进入编辑页面
                        </p>
                        <div className="video-status-actions">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="video-status-button video-status-button--primary"
                            >
                                返回视频列表
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    // 刷新页面，重新获取视频状态
                                    window.location.reload();
                                }}
                                className="video-status-button video-status-button--success"
                            >
                                刷新状态
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {showPageGlass && (
                        <div
                            className="video-edit-resources-overlay"
                            role="status"
                            aria-live="polite"
                            aria-label={isLoadingVideo ? '加载任务与字幕中' : '视频预览加载中'}
                        >
                            <Spin size="large" />
                        </div>
                    )}
                    {/* 背景音乐音量调节弹窗 */}
                    <VolumeAdjustmentPopup
                        visible={showBackgroundVolumePopup}
                        onClose={() => setShowBackgroundVolumePopup(false)}
                        onConfirm={async (volume) => {
                            // 1. 先本地预览：通过 Web Audio GainNode 放大背景（确认按钮在用户手势内）
                            setBackgroundVolume(volume);
                            ensureBackgroundGainNode();
                            resumeBackgroundAudioContextIfSuspended();
                            if (backgroundGainNodeRef.current) {
                                backgroundGainNodeRef.current.gain.value = volume;
                                console.log('🔊 背景音预览增益更新为:', volume);
                            }

                            // 2. 调用后端接口调节任务背景音音
                            if (!videoData?.id) {
                                console.error('调节背景音失败：缺少任务ID');
                                showError('调节背景音失败：缺少任务ID');
                                return;
                            }

                            try {
                                // 这里将弹窗中的数值直接作 volumeGainDb 传给后端
                                const response = await adjustTaskVolumeApi({
                                    id: videoData.id,
                                    volumeGainDb: volume,
                                });

                                if (response.code === 0 && response.data) {
                                    showSuccess('背景音音量已更新');
                                } else {
                                    console.error('调节背景音失败：', response);
                                    showError(response.msg || '调节背景音失败');
                                }
                            } catch (error: any) {
                                console.error('调用 /video/subtitles/adjust-task-volume 接口失败：', error);
                                showError(error?.message || '调节背景音失败，请稍后重试');
                            }
                        }}
                        initialVolume={backgroundVolume}
                        position={backgroundVolumePopupPosition}
                        loading={false}
                        showApplyToAll={false}
                    />

                    {/* 主要内容区域 */}
                    <div className='caozuo-title'>
                        <div className="home-series__header-leading">
                            <PageBackButton
                                onClick={handleBack}
                                aria-label="返回视频列表"
                            />
                        </div>
                        <span>{videoName}</span>
                        <div className="generate-button-container">
                            {isFromPure ? (                                <button 
                                    className="generate-btn" 
                                    onClick={handleProofread}
                                    disabled={isGenerating || !videoData || showPageGlass}
                                >
                                    {isGenerating && <i className="fas fa-spinner fa-spin generate-btn-spinner"></i>}
                                    {isGenerating ? '校对中..' : '已校对'}
                                </button>
                            ) : (
                                <button 
                                    className="generate-btn" 
                                    onClick={handleGenerate}
                                    disabled={isGenerating || !videoData || showPageGlass}
                                >
                                    {isGenerating && <i className="fas fa-spinner fa-spin generate-btn-spinner"></i>}
                                    {isGenerating ? '合成中...' : '合成'}
                                </button>
                            )}
                        </div>
                    </div>
                    <main className="video-edit-content">
                        <div className="video-left">

                            <div className="top">视频预览</div>
                                <VideoShow
                                    orientation={orientation}
                                    leftProps={{
                                        isAdmin,
                                        isUser,
                                        displayName,
                                        sourceVideoRef,
                                        videoSrc: videoData?.video_hls_url?.includes('.m3u8') ? undefined : (videoData?.video_hls_url || undefined),
                                        isLoadingVideo: false,
                                        hasVideoData: !!videoData?.video_hls_url,
                                        onRefetchVideoData: async () => {
                                            try {
                                                setIsLoadingVideo(true);
                                                const taskData = await getTaskStatusApi(parseInt(id!));
                                                setVideoDataSafe(taskData);
                                                setTimeout(() => {
                                                    const event = new Event('videoDataUpdated');
                                                    window.dispatchEvent(event);
                                                }, 100);
                                            } catch (error) {
                                                console.error('【左侧视频显示】重新获取视频数据失败', error);
                                            } finally {
                                                setIsLoadingVideo(false);
                                            }
                                        },
                                        onTimeUpdate: handleTimeUpdate,
                                        onLoadedMetadata: handleLoadedMetadata,
                                        onEnded: handleVideoEnded,
                                        onPlay: () => {
                                            setIsPlaying(true);
                                            if (!userInteractionRef.current) {
                                                ensureAudioPlayAndSync();
                                            }
                                            userInteractionRef.current = false;
                                        },
                                        onPause: () => {
                                            setIsPlaying(false);
                                            if (!userInteractionRef.current) {
                                                if (backgroundAudioRef.current) {
                                                    backgroundAudioRef.current.pause();
                                                }
                                            }
                                            if (vocalsAudioRef.current) {
                                                vocalsAudioRef.current.pause();
                                            }
                                            if (vocalsHlsRef.current) {
                                                vocalsHlsRef.current.destroy();
                                                vocalsHlsRef.current = null;
                                            }
                                            userInteractionRef.current = false;
                                        },
                                        onPauseAdmin: () => {
                                            setIsPlaying(false);
                                            if (!userInteractionRef.current) {
                                                if (backgroundAudioRef.current) {
                                                    backgroundAudioRef.current.pause();
                                                }
                                            }
                                            userInteractionRef.current = false;
                                        },
                                        onError: (e: any) => {
                                            console.error('【左侧视频显示】左侧视频加载错误?', e);
                                            if (sourceVideoRef.current && videoData?.video_hls_url) {
                                                setTimeout(() => {
                                                    const event = new Event('videoDataUpdated');
                                                    window.dispatchEvent(event);
                                                }, 1000);
                                            }
                                        }
                                    }}
                                    rightProps={{
                                        isAdmin,
                                        isUser,
                                        displayName,
                                        subtitleOverlayPlayerRef,
                                        isLoadingVideo: rightCellLoadingUi,
                                        videoDataForDemo,
                                        localSubtitlesForDemo,
                                        targetTextForDemo,
                                        id,
                                        overlayTopPx: isAdmin ? 10 : 0,
                                        interactive: !isFromPure,
                                        overlayDisplayLockLineId:
                                            highlightedSubtitleId ?? timelineSelectedSubtitleId ?? null,
                                        onExternalPlay: handleExternalPlay,
                                        onExternalPause: handleExternalPause,
                                        onExternalSeek: handleExternalSeek,
                                        onRightVideoReady: handleRightVideoReady,
                                        onSubtitlePostTextChange: handleSubtitlePostTextChange,
                                    }}
                                />
                        </div>
                        <div className="video-right">
                            <div className="top">
                                <span className="top-title">{isFromPure ? '说话人与字幕校对' : '台词编辑'}</span>
                                {!isFromPure && (
                                    <div className="video-edit-top-actions">
                                        <div className="global-retranslate-container">
                                            <div
                                                className="global-retranslate-trigger"
                                                onMouseEnter={clearGlobalRetranslateCloseTimer}
                                                onMouseLeave={scheduleCloseGlobalRetranslateDropdown}
                                            >
                                                <button
                                                    className={`global-retranslate-btn ${isGlobalRetranslating ? 'disabled' : ''}`}
                                                    onClick={() => {
                                                        handleGlobalRetranslate("维持原语义，贴合场景");
                                                    }}
                                                    disabled={isGlobalRetranslating}
                                                >
                                                    <span>{isGlobalRetranslating ? '翻译中..' : '重新翻译'}</span>
                                                    <span
                                                        className="global-retranslate-arrow"
                                                        onMouseEnter={openGlobalRetranslateDropdown}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <i className={`fas ${isArrowHovered ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                                    </span>
                                                </button>
                                                <div
                                                    className="global-retranslate-dropdown-wrapper"
                                                    onMouseEnter={openGlobalRetranslateDropdown}
                                                    onMouseLeave={scheduleCloseGlobalRetranslateDropdown}
                                                >
                                                    <TranslationAdjustment
                                                        showDropdown={showGlobalRetranslateDropdown}
                                                        isTranslating={isGlobalRetranslating}
                                                        onDropdownChange={setShowGlobalRetranslateDropdown}
                                                        onTranslate={handleGlobalRetranslate}
                                                        customText={globalCustomText}
                                                        onCustomTextChange={setGlobalCustomText}
                                                        buttonText="重新翻译"
                                                        buttonClassName="global-retranslate-btn"
                                                        dropdownOffset={{ top: '-2px', left: '0' }}
                                                        customCardOffset={{ top: '-2px', left: '130px' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            className={`back-translate-btn ${isBackTranslating ? 'disabled' : ''}`}
                                            onClick={async () => {
                                                try {
                                                    if (videoData?.id) {
                                                        setIsBackTranslating(true);
                                                        const response = await backTranslateApi(videoData.id);
                                                        if (response.code === 0 && response.data) {
                                                            setBackTranslateData(response.data);
                                                            setVideoData(prev => {
                                                                if (!prev?.subtitles) return prev;
                                                                const updatedSubtitles = prev.subtitles.map(subtitle => {
                                                                    const backTranslateItem = response.data.find(item => item.id === subtitle.id);
                                                                    if (backTranslateItem?.backTranslateText) {
                                                                        return {
                                                                            ...subtitle,
                                                                            back_translate_text: backTranslateItem.backTranslateText
                                                                        };
                                                                    }
                                                                    return subtitle;
                                                                });
                                                                return {
                                                                    ...prev,
                                                                    subtitles: updatedSubtitles
                                                                };
                                                            });
                                                            showSuccess('回译成功');
                                                        } else {
                                                            showError(response.msg || '回译失败');
                                                        }
                                                    }
                                                } catch (error: any) {
                                                    console.error('一键回译失败', error);
                                                    showError(error?.message || '回译失败，请重试');
                                                } finally {
                                                    setIsBackTranslating(false);
                                                }
                                            }}
                                            disabled={isBackTranslating}
                                        >
                                            {isBackTranslating ? '回译中..' : '一键回译'}
                                        </button>                                        {videoData?.dubbingMethod !== 3 ? (
                                            <button
                                                className={`global-redub-btn ${isGlobalRedubbing ? 'disabled' : ''}`}
                                                onClick={handleGlobalRedub}
                                                disabled={isGlobalRedubbing}
                                            >
                                                {isGlobalRedubbing ? '配音中..' : '重新配音'}
                                            </button>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            {!isFromPure && (
                            <div className="video-title">
                                <div></div>
                                <div className='firstP-container'>
                                    <p className='firstP'>自动检测</p>
                                </div>
                                <div className='secondP-container'>
                                    <p className='secondP'>
                                        {langNameFromUrl || getLanguageName(videoData?.langCode || videoData?.lang_code || currentEpisodeInfo.targetLanguage) || '未知语言'}
                                    </p>
                                </div>
                                 <p className='thirdP'>留言</p>
                              </div>
                            )}
                            <VideoTextList
                                textListRef={textListRef}
                                listProps={{
                                    sourcesText: sourcesText as any,
                                    targetText: targetText as any,
                                    videoData: videoData,
                                    roles: roles,
                                    rowSelectedRoles: rowSelectedRoles,
                                    editingRoleKey: editingRoleKey,
                                    editingRoleName: editingRoleName,
                                    onEditingRoleNameChange: setEditingRoleName,
                                    seriesSpeakers: seriesSpeakers,
                                    langCode: videoData?.langCode || videoData?.lang_code || currentEpisodeInfo.targetLanguage,
                                    highlightedSubtitleId: highlightedSubtitleId,
                                    isFromPure: isFromPure,
                                    isGlobalRetranslating: isGlobalRetranslating,
                                    isGlobalRedubbing: isGlobalRedubbing,
                                    subtitleProcessingStates: subtitleProcessingStates as any,
                                    updatingSubtitles: updatingSubtitles as any,
                                    subtitleUpdateErrors: subtitleUpdateErrors as any,
                                    seriesVoiceLibraryMarkers: seriesVoiceLibraryMarkers as any,
                                    backTranslateData: backTranslateData as any,
                                    openHistorySubtitleId: openHistorySubtitleId,
                                    volumeMap: volumeMap,
                                    speedMap: speedMap,
                                    onUpdateVolume: handleUpdateVolume as any,
                                    onUpdateSpeed: handleUpdateSpeed as any,
                                    onSourceDataChange: handleSourceDataChange as any,
                                    onTargetDataChange: handleTargetDataChange as any,
                                    onTargetDialogueClick: handleTargetDialogueClick,
                                    onRoleSelect: handleRoleSelect as any,
                                    onNewSpeaker: handleNewSpeaker as any,
                                    onDeleteRole: handleDeleteRole as any,
                                    onStartEditRole: handleStartEditRole as any,
                                    onSaveRoleEdit: handleSaveRoleEdit as any,
                                    onCancelRoleEdit: handleCancelRoleEdit as any,
                                    onPromoteToSeriesSpeaker: handlePromoteToSeriesSpeaker as any,
                                    onSeekToTime: seekToTime as any,
                                    onAudioPause: pauseAudioForEditing as any,
                                    onAudioSync: syncAudioAfterEditing as any,
                                    onPlaySubtitle: playSubtitle as any,
                                    onPlayTargetSubtitle: playTargetSubtitle as any,
                                    onUpdateVideoDataSpeed: handleUpdateVideoDataSpeed as any,
                                    onSwitchTrack: handleSwitchTrack as any,
                                    onDeleteSubtitle: handleDeleteSubtitle as any,
                                    onAddSubtitle: handleAddSubtitle,
                                    onDeleteSeriesSpeaker: (speakerId: number) => {
                                        setSeriesSpeakers(prev => prev.filter(s => s.id !== speakerId));
                                    },
                                    onSubtitleUpdate: handleSubtitleUpdate as any,
                                    onGetNextSubtitle: handleGetNextSubtitle as any,
                                    onGetNextSubtitleId: handleGetNextSubtitleId as any,
                                    onGetFullTimelineData: handleGetFullTimelineData as any,
                                    onRefreshSubtitles: handleRefreshSubtitles as any,
                                    onOpenHistoryPanel: handleOpenHistoryPanel as any,
                                    onCloseHistoryPanel: handleCloseHistoryPanel as any
                                }}
                            />
                        </div>


                    </main>
                    
                    <VideoTimeBox
                        isPlaying={isPlaying}
                        togglePlay={togglePlay}
                        formatTime={formatTime}
                        duration={duration}
                        zoomLevel={zoomLevel}
                        handleZoomOut={handleZoomOut}
                        handleZoomIn={handleZoomIn}
                        handleZoomChange={handleZoomChange}
                        handleZoomAfterChange={handleZoomAfterChange}
                        isFromPure={isFromPure}
                        backgroundAudioEnabled={backgroundAudioEnabled}
                        toggleBackgroundAudio={toggleBackgroundAudio}
                        setBackgroundVolumePopupPosition={setBackgroundVolumePopupPosition}
                        backgroundVolume={backgroundVolume}
                        videoData={videoData}
                        setBackgroundVolume={setBackgroundVolume}
                        setShowBackgroundVolumePopup={setShowBackgroundVolumePopup}
                        sourceVideoRef={sourceVideoRef}
                        playheadTotalTimeRef={playheadTotalTimeRef}
                        timelineScrubbingRef={timelineScrubbingRef}
                        translatedTimelineModels={translatedTimelineModels}
                        timelineActiveTrack={timelineActiveTrack}
                        timelineSelectedSubtitleId={timelineSelectedSubtitleId}
                        timelineEditorRef={timelineEditorRef}
                        getTimelineTotalTimeSec={getTimelineTotalTimeSec}
                    />
                    
                    {/* 生成中的遮罩*/}
                    {isGenerating && (
                        <div 
                            className="generating-overlay"
                            onClick={(e) => e.preventDefault()}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseUp={(e) => e.preventDefault()}
                            onTouchStart={(e) => e.preventDefault()}
                            onTouchEnd={(e) => e.preventDefault()}
                        />
                    )}

                </>
            )}            
            {/* 隐藏的音频元*/}
            <audio 
                ref={backgroundAudioRef} 
                preload="auto" 
                crossOrigin="anonymous"
            />
        </div>
    );
};

export default VideoEdit;
